import React, { useState, useEffect, useMemo } from "react";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Input,
  Label,
  Row,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  FormGroup,
} from "reactstrap";
import { getActivities } from "../../components/Api/GmsActivity";
import { getActivityConfigurations, saveActivityConfiguration } from "../../components/Api/GmsActivityConfiguration";
import { getServices } from "../../components/Api/GmsService";
import { getGroups } from "../../components/Api/GmsGroup";
import { getTaxes } from "../../components/Api/GmsTax";
import { Eye, SquarePen } from "lucide-react";
import { ClipLoader } from "react-spinners";
import Swal from "sweetalert2";
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission, 
  hasDeletePermissionWithAdmin, 
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

// Custom styles for Select components
const selectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: '38px',
    borderColor: state.isFocused ? '#556ee6' : '#ced4da',
    boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(85, 110, 230, 0.25)' : 'none',
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isSelected ? '#556ee6' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#495057',
    '&:hover': {
      backgroundColor: state.isSelected ? '#556ee6' : '#e9ecef',
    },
  }),
};

const defaultForm = {
  configId: 0,
  group_Id: 0,
  service_Id: 0,
  activity_Id: 0,
  rate: 0,
  act_Description: "",
  status: true,
  createdBy: "",
  createdOn: new Date().toISOString(),
  modifiedBy: "",
  modifiedOn: new Date().toISOString(),
  taxIds: [],
};

const emptyFilter = {
  configId: null,
  group_Id: null,
  service_Id: null,
  activity_Id: null,
  status: null,
};

export default function ActivityUpdatedPage() {
  const [configurations, setConfigurations] = useState([]);
  const [activities, setActivities] = useState([]);
  const [services, setServices] = useState([]);
  const [groups, setGroups] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(emptyFilter);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [viewLoadingRow, setViewLoadingRow] = useState(null);
  const [editLoadingRow, setEditLoadingRow] = useState(null);
  const [activityOptions, setActivityOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [groupOptions, setGroupOptions] = useState([]);
  const [taxOptions, setTaxOptions] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [activeTab, setActiveTab] = useState("1");
  const [hasAppliedFilter, setHasAppliedFilter] = useState(false);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsActivityUpdated');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsActivityUpdated');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsActivityUpdated');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsActivityUpdated');
  const isAdmin = isUserAdmin();

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  const formatDateTime = (dateTimeStr) => {
    try {
      return dateTimeStr.split('T')[0];
    } catch (error) {
      return "N/A";
    }
  };

  const formatFullDateTime = (dateTimeStr) => {
    try {
      const date = new Date(dateTimeStr);
      return date.toLocaleString();
    } catch (error) {
      return "N/A";
    }
  };

  useEffect(() => {
    console.log('Component mounted');
    if (hasPermission) {
      console.log('Loading data due to permission');
      loadActivities();
      loadServices();
      loadGroups();
      loadTaxes();
      loadConfigurations(); // Load configurations on component mount
      
      // Set default tab based on permissions
      if (isViewOnly) {
        setActiveTab("2"); // Show list tab for view-only users
      }
    } else {
      console.log('No permission to view page');
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, isViewOnly]);

  useEffect(() => {
    if (hasAppliedFilter) {
      loadConfigurations();
    }
  }, [hasAppliedFilter]);

  const loadConfigurations = async (filterData = filter) => {
    setLoading(true);
    try {
      const data = await getActivityConfigurations(filterData);
      setConfigurations(data);
      setTotalCount(data.length);
    } catch (error) {
      Swal.fire("Error", "Failed to load configurations: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const loadActivities = async () => {
    try {
      const data = await getActivities({});
      const options = data.map((activity) => ({
        value: activity.activity_Id,
        label: activity.activity_NameEn,
      }));
      setActivityOptions(options);
      setActivities(data);
    } catch (error) {
      Swal.fire("Error", "Failed to load activities: " + error.message, "error");
    }
  };

  const loadServices = async () => {
    try {
      const data = await getServices({});
      const options = data.map((service) => ({
        value: service.service_Id,
        label: service.service_NameEN,
      }));
      setServiceOptions(options);
      setServices(data);
    } catch (error) {
      Swal.fire("Error", "Failed to load services: " + error.message, "error");
    }
  };

  const loadGroups = async () => {
    try {
      const data = await getGroups({});
      const options = data.map((group) => ({
        value: group.group_Id,
        label: group.group_Name,
      }));
      setGroupOptions(options);
      setGroups(data);
    } catch (error) {
      Swal.fire("Error", "Failed to load groups: " + error.message, "error");
    }
  };

  const loadTaxes = async () => {
    try {
      const data = await getTaxes({});
      const options = data.map((tax) => ({
        value: tax.tax_Id,
        label: tax.tax_NameEn,
      }));
      setTaxOptions(options);
      setTaxes(data);
    } catch (error) {
      Swal.fire("Error", "Failed to load taxes: " + error.message, "error");
    }
  };

  const handleFormChange = (field, value) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.group_Id || !form.service_Id || !form.activity_Id || !form.rate) {
      Swal.fire("Error", "Please fill in all required fields", "error");
      return;
    }

    setSaveLoading(true);
    try {
      const session = getUserSession();
      const configurationData = {
        ...form,
        createdBy: isEditing ? form.createdBy : session.userName,
        modifiedBy: session.userName,
        modifiedOn: new Date().toISOString(),
      };

      const result = await saveActivityConfiguration(configurationData);
      
      if (result.id) {
        Swal.fire("Success", "Configuration saved successfully!", "success");
        handleClear();
        if (hasAppliedFilter) {
          loadConfigurations();
        }
      }
    } catch (error) {
      let errorMessage = "Failed to save configuration";
      
      // Extract error message from API response
      if (error.response && error.response.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.msg) {
          errorMessage = error.response.data.msg;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = (configuration) => {
    setEditLoadingRow(configuration.configId);
    try {
      setForm({
        configId: configuration.configId,
        group_Id: configuration.group_Id || 0,
        service_Id: configuration.service_Id || 0,
        activity_Id: configuration.activity_Id || 0,
        rate: configuration.rate || 0,
        act_Description: configuration.act_Description || "",
        status: configuration.status,
        createdBy: configuration.createdBy || "",
        createdOn: configuration.createdOn,
        modifiedBy: configuration.modifiedBy || "",
        modifiedOn: configuration.modifiedOn,
        taxIds: configuration.taxIds || [],
      });
      setIsEditing(true);
      setActiveTab("1"); // Switch to form tab
    } catch (error) {
      Swal.fire("Error", "Failed to load configuration data", "error");
    } finally {
      setEditLoadingRow(null);
    }
  };

  const handleClear = () => {
    setForm(defaultForm);
    setIsEditing(false);
  };

  const handleApplyFilter = () => {
    setHasAppliedFilter(true);
    setPage(1);
    loadConfigurations(filter);
  };

  const handleClearFilter = () => {
    setFilter(emptyFilter);
    setHasAppliedFilter(false);
    setConfigurations([]);
    setTotalCount(0);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const getPaginatedConfigurations = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return configurations.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    return Math.ceil(totalCount / pageSize);
  };

  const toggle = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      // Refresh configurations when switching to Activity List tab
      if (tab === "2") {
        loadConfigurations();
      }
    }
  };

  const getActivityName = (activityId) => {
    const activity = activities.find(a => a.activity_Id === activityId);
    return activity ? activity.activity_NameEn : "N/A";
  };

  const getServiceName = (serviceId) => {
    const service = services.find(s => s.service_Id === serviceId);
    return service ? service.service_NameEN : "N/A";
  };

  const getGroupName = (groupId) => {
    const group = groups.find(g => g.group_Id === groupId);
    return group ? group.group_Name : "N/A";
  };

  const getTaxNames = (taxIds) => {
    if (!taxIds || taxIds.length === 0) return "N/A";
    const taxNames = taxIds.map(taxId => {
      const tax = taxes.find(t => t.tax_Id === taxId);
      return tax ? tax.tax_NameEn : "Unknown";
    });
    return taxNames.join(", ");
  };

  const selectedActivity = activityOptions.find(a => a.value === form.activity_Id);
  const selectedService = serviceOptions.find(s => s.value === form.service_Id);
  const selectedGroup = groupOptions.find(g => g.value === form.group_Id);
  const selectedTaxes = taxOptions.filter(t => form.taxIds.includes(t.value));

  return (
    <div className="page-content">
      <Container fluid>
                                <Breadcrumb title="Activities" breadcrumbItem="Spa" />

        <Row>
          <Col xs={12}>
            <Card>
              <CardBody className="p-0">
                <Nav tabs className="nav-tabs-custom">
                  {!isViewOnly && (
                    <NavItem>
                      <NavLink
                        style={{ 
                          cursor: "pointer",
                          fontWeight: activeTab === "1" ? "bold" : "normal",
                          color: activeTab === "1" ? "#556ee6" : "#74788d",
                          borderBottom: activeTab === "1" ? "2px solid #556ee6" : "none",
                          padding: "15px 25px",
                          fontSize: "16px",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px"
                        }}
                        className={activeTab === "1" ? "active" : ""}
                        onClick={() => toggle("1")}
                      >
                        <i className="bx bx-plus-circle"></i>
                        <span>Configuration Form</span>
                      </NavLink>
                    </NavItem>
                  )}
                  <NavItem>
                    <NavLink
                      style={{ 
                        cursor: "pointer",
                        fontWeight: activeTab === "2" ? "bold" : "normal",
                        color: activeTab === "2" ? "#556ee6" : "#74788d",
                        borderBottom: activeTab === "2" ? "2px solid #556ee6" : "none",
                        padding: "15px 25px",
                        fontSize: "16px",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                      }}
                      className={activeTab === "2" ? "active" : ""}
                      onClick={() => toggle("2")}
                    >
                      <i className="bx bx-list-ul"></i>
                      <span>Configuration List</span>
                    </NavLink>
                  </NavItem>
                </Nav>
                <TabContent activeTab={activeTab} className="p-4">
                  {!isViewOnly && (
                    <TabPane tabId="1">
                      <div>
                        <Form onSubmit={handleSubmit}>
                        {/* Dropdown Fields Section */}
                        <Row>
                          <Col md={4}>
                            <FormGroup>
                              <Label for="group_Id">Group <span className="text-danger">*</span></Label>
                              <Select
                                id="group_Id"
                                value={selectedGroup || null}
                                onChange={(selected) => handleFormChange("group_Id", selected?.value || 0)}
                                options={groupOptions}
                                placeholder="Select Group"
                                isClearable
                                isSearchable
                                styles={selectStyles}
                                isDisabled={isEditing}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label for="service_Id">Service <span className="text-danger">*</span></Label>
                              <Select
                                id="service_Id"
                                value={selectedService || null}
                                onChange={(selected) => handleFormChange("service_Id", selected?.value || 0)}
                                options={serviceOptions}
                                placeholder="Select Service"
                                isClearable
                                isSearchable
                                styles={selectStyles}
                                isDisabled={isEditing}
                              />
                            </FormGroup>
                          </Col>
                          <Col md={4}>
                            <FormGroup>
                              <Label for="activity_Id">Activity <span className="text-danger">*</span></Label>
                              <Select
                                id="activity_Id"
                                value={selectedActivity || null}
                                onChange={(selected) => handleFormChange("activity_Id", selected?.value || 0)}
                                options={activityOptions}
                                placeholder="Select Activity"
                                isClearable
                                isSearchable
                                styles={selectStyles}
                                isDisabled={isEditing}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        
                        {/* Price and Status Fields Section */}
                        <Row className="mt-3 align-items-center ">
                          <Col md={6}>
                            <FormGroup>
                              <Label for="rate">Price <span className="text-danger">*</span></Label>
                              <Input
                                type="number"
                                id="rate"
                                value={form.rate}
                                onChange={(e) => handleFormChange("rate", parseFloat(e.target.value) || 0)}
                                placeholder="Enter price"
                                min="0"
                                step="0.01"
                              />
                            </FormGroup>
                          </Col>
                          <Col md={6}>
                            <FormGroup>
                              <div className="form-check form-switch mt-5">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="status"
                                  checked={form.status}
                                  onChange={(e) => handleFormChange("status", e.target.checked)}
                                  disabled={!hasDeletePermission}
                                />
                                <label className="form-check-label" htmlFor="status">
                                  {form.status ? "Active" : "Inactive"}
                                </label>
                              </div>
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row className="mt-3">
                          <Col md={12}>
                            <FormGroup>
                              <Label for="taxIds">Taxes (Optional)</Label>
                              <Select
                                id="taxIds"
                                value={selectedTaxes}
                                onChange={(selected) => handleFormChange("taxIds", selected ? selected.map(t => t.value) : [])}
                                options={taxOptions}
                                placeholder="Select Taxes"
                                isMulti
                                isClearable
                                isSearchable
                                styles={selectStyles}
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row className="mt-3">
                          <Col md={12}>
                            <FormGroup>
                              <Label for="act_Description">Description (Optional)</Label>
                              <Input
                                type="textarea"
                                id="act_Description"
                                rows="3"
                                value={form.act_Description}
                                onChange={(e) => handleFormChange("act_Description", e.target.value)}
                                placeholder="Enter configuration description"
                              />
                            </FormGroup>
                          </Col>
                        </Row>
                        <Row className="mt-3">
                          <Col md={12}>
                            <div className="d-flex justify-content-end">
                              <Button color="primary" type="submit" disabled={saveLoading} className="me-2">
                                {saveLoading ? <ClipLoader size={20} color="white" /> : (isEditing ? "Update" : "Save")}
                              </Button>
                              <Button color="secondary" type="button" onClick={handleClear}>
                                Clear
                              </Button>
                            </div>
                          </Col>
                        </Row>
                        </Form>
                      </div>
                    </TabPane>
                  )}
                  <TabPane tabId="2">
                    <div>
                      {/* Filter Section */}
                      <Card>
                        <CardBody>
                          <h5 className="card-title">Filter Configurations</h5>
                          <Row>
                            <Col md={4}>
                              <FormGroup>
                                <Label for="filter_activity_Id">Activity</Label>
                                <Select
                                  id="filter_activity_Id"
                                  value={activityOptions.find(a => a.value === filter.activity_Id) || null}
                                  onChange={(selected) => handleFilterChange("activity_Id", selected?.value || null)}
                                  options={activityOptions}
                                  placeholder="Select Activity"
                                  isClearable
                                  isSearchable
                                  styles={selectStyles}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={4}>
                              <FormGroup>
                                <Label for="filter_service_Id">Service</Label>
                                <Select
                                  id="filter_service_Id"
                                  value={serviceOptions.find(s => s.value === filter.service_Id) || null}
                                  onChange={(selected) => handleFilterChange("service_Id", selected?.value || null)}
                                  options={serviceOptions}
                                  placeholder="Select Service"
                                  isClearable
                                  isSearchable
                                  styles={selectStyles}
                                />
                              </FormGroup>
                            </Col>
                            <Col md={4}>
                              <FormGroup>
                                <Label for="filter_group_Id">Group</Label>
                                <Select
                                  id="filter_group_Id"
                                  value={groupOptions.find(g => g.value === filter.group_Id) || null}
                                  onChange={(selected) => handleFilterChange("group_Id", selected?.value || null)}
                                  options={groupOptions}
                                  placeholder="Select Group"
                                  isClearable
                                  isSearchable
                                  styles={selectStyles}
                                />
                              </FormGroup>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={4}>
                              <FormGroup>
                                <Label for="filter_activity_NameEn">Activity Name</Label>
                                <Input
                                  type="text"
                                  id="filter_activity_NameEn"
                                  value={filter.activity_NameEn}
                                  onChange={(e) => handleFilterChange("activity_NameEn", e.target.value)}
                                  placeholder="Enter activity name"
                                />
                              </FormGroup>
                            </Col>
                            <Col md={4}>
                              <FormGroup>
                                <Label for="filter_activity_Code">Activity Code</Label>
                                <Input
                                  type="text"
                                  id="filter_activity_Code"
                                  value={filter.activity_Code}
                                  onChange={(e) => handleFilterChange("activity_Code", e.target.value)}
                                  placeholder="Enter activity code"
                                />
                              </FormGroup>
                            </Col>
                            <Col md={4}>
                              <div className="d-flex justify-content-end align-items-end" style={{height: '100%'}}>
                                <div>
                                  <Button color="secondary" type="button" onClick={handleClearFilter} className="me-2">
                                    Clear Filter
                                  </Button>
                                  <Button color="primary" type="button" onClick={handleApplyFilter}>
                                    Apply Filter
                                  </Button>
                                </div>
                              </div>
                            </Col>
                          </Row>
                        </CardBody>
                      </Card>

                      {/* Activities Table */}
                      <Card>
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="card-title mb-0">
                              Activity List ({totalCount} records)
                            </h5>
                            <div className="d-flex align-items-center">
                              <Label className="me-2">Show:</Label>
                              <Input
                                type="select"
                                value={pageSize}
                                onChange={(e) => {
                                  setPageSize(parseInt(e.target.value));
                                  setPage(1);
                                }}
                                style={{ width: "auto" }}
                              >
                                <option value="10">10</option>
                                <option value="25">25</option>
                                <option value="50">50</option>
                                <option value="100">100</option>
                              </Input>
                            </div>
                          </div>
                          
                          {loading ? (
                            <div className="text-center">
                              <ClipLoader size={50} color="black" />
                            </div>
                          ) : (
                            <>
                              <div className="table-responsive">
                                <Table bordered>
                                  <thead className="table-light">
                                    <tr>
                                      <th>Sr.No</th>
                                      <th>Activity Name</th>
                                      <th>Service Name</th>
                                      <th>Group Name</th>
                                      <th>Price</th>
                                      <th>Taxes</th>
                                      <th>Description</th>
                                      <th>Status</th>
                                      <th>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {configurations.length === 0 ? (
                                      <tr>
                                        <td colSpan="9" className="text-center">
                                          No configurations found
                                        </td>
                                      </tr>
                                    ) : (
                                      getPaginatedConfigurations().map((configuration, index) => (
                                        <tr key={configuration.configId}>
                                          <td>{(page - 1) * pageSize + index + 1}</td>
                                          <td>{getActivityName(configuration.activity_Id)}</td>
                                          <td>{getServiceName(configuration.service_Id)}</td>
                                          <td>{getGroupName(configuration.group_Id)}</td>
                                          <td className="text-end">{configuration.rate ? configuration.rate.toFixed(2) : '0.00'}</td>
                                          <td>{getTaxNames(configuration.taxIds)}</td>
                                          <td>{configuration.act_Description || "N/A"}</td>
                                          <td>
                                            <span className={`badge ${configuration.status ? 'bg-success' : 'bg-secondary'}`} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                              {configuration.status ? "Active" : "Inactive"}
                                            </span>
                                          </td>
                                          <td>
                                            {!isViewOnly && (
                                              <div className="d-flex gap-2">
                                                <Button
                                                  color="info"
                                                  size="sm"
                                                  onClick={() => handleEdit(configuration)}
                                                  disabled={editLoadingRow === configuration.configId}
                                                >
                                                  {editLoadingRow === configuration.configId ? (
                                                    <ClipLoader size={16} color="white" />
                                                  ) : (
                                                    "Edit"
                                                  )}
                                                </Button>
                                              </div>
                                            )}
                                          </td>
                                        </tr>
                                      ))
                                    )}
                                  </tbody>
                                </Table>
                              </div>

                              {/* Pagination - Similar to BranchPage style */}
                              {totalCount > pageSize && (
                                <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                                     style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
                                  <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                                    <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                                    Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
                                  </div>
                                  <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                                    <Button
                                      color="light"
                                      size="sm"
                                      disabled={page === 1}
                                      onClick={() => handlePageChange(page - 1)}
                                      style={{
                                        border: '1px solid #dee2e6',
                                        borderRadius: '0.375rem',
                                        minWidth: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                                        color: page === 1 ? '#6c757d' : '#495057'
                                      }}
                                    >
                                      <i className="mdi mdi-chevron-left"></i>
                                    </Button>
                                    
                                    {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                                      const pageNum = index + 1;
                                      const isActive = page === pageNum;
                                      
                                      return (
                                        <Button
                                          key={pageNum}
                                          color={isActive ? "primary" : "light"}
                                          size="sm"
                                          onClick={() => handlePageChange(pageNum)}
                                          style={{
                                            border: '1px solid #dee2e6',
                                            borderRadius: '0.375rem',
                                            minWidth: '32px',
                                            height: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: isActive ? '#556ee6' : '#fff',
                                            borderColor: isActive ? '#556ee6' : '#dee2e6',
                                            color: isActive ? '#fff' : '#495057',
                                            fontWeight: isActive ? '600' : '400',
                                            boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                                          }}
                                        >
                                          {pageNum}
                                        </Button>
                                      );
                                    })}
                                    
                                    <Button
                                      color="light"
                                      size="sm"
                                      disabled={page === Math.ceil(totalCount / pageSize)}
                                      onClick={() => handlePageChange(page + 1)}
                                      style={{
                                        border: '1px solid #dee2e6',
                                        borderRadius: '0.375rem',
                                        minWidth: '32px',
                                        height: '32px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                                        color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                                      }}
                                    >
                                      <i className="mdi mdi-chevron-right"></i>
                                    </Button>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </CardBody>
                      </Card>
                    </div>
                  </TabPane>
                </TabContent>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
} 